import React from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { DynamicField } from '../core/types/DynamicField.types';
import { Button } from '../../Button/Button';
import { PencilSquareIcon, TrashIcon, Bars3Icon, InformationCircleIcon, EyeIcon } from '@heroicons/react/24/outline';

const GripIcon = () => (
    <div className="text-gray-400 cursor-grab active:cursor-grabbing hover:text-gray-600 transition-colors p-1">
        <Bars3Icon className="w-5 h-5" />
    </div>
);

interface SortableItemProps {
    id: string;
    field: DynamicField;
    onEdit: (field: DynamicField) => void;
    onDelete: (id: string) => void;
    isReadonly?: boolean;
}

const SortableItem: React.FC<SortableItemProps> = ({ id, field, onEdit, onDelete, isReadonly }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id, disabled: isReadonly });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div ref={setNodeRef} style={style} className="flex items-center border-b border-gray-200 py-2 group hover:bg-gray-50/50 transition-colors bg-white">
            <div className="flex flex-col items-center justify-center min-h-[56px] px-4 py-2 shrink-0">
                {!isReadonly && (
                    <div {...attributes} {...listeners} className="outline-none">
                        <GripIcon />
                    </div>
                )}
                {isReadonly && <div className="w-7" />}
            </div>
            <div className="flex flex-1 flex-col items-start justify-center min-h-[56px] px-4 py-2">
                <p className="leading-[20px] text-sm text-gray-900 font-medium">
                    {field.labels['es'] || field.code}
                </p>
            </div>
            <div className="flex items-center gap-2 min-h-[56px] px-4 py-2 shrink-0 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                {isReadonly ? (
                    <Button
                        type="plain"
                        iconOnly
                        leftIcon={<EyeIcon className="w-5 h-5 text-gray-400 hover:text-gray-600" />}
                        ariaLabel="Ver campo"
                        onClick={() => onEdit(field)}
                    />
                ) : (
                    <>
                        <Button
                            type="plain"
                            iconOnly
                            leftIcon={<PencilSquareIcon className="w-5 h-5 text-[#0e79fd] hover:text-blue-700" />}
                            ariaLabel="Editar campo"
                            onClick={() => onEdit(field)}
                        />
                        <Button
                            type="plain"
                            iconOnly
                            leftIcon={<TrashIcon className="w-5 h-5 text-red-500 hover:text-red-700" />}
                            ariaLabel="Eliminar campo"
                            onClick={() => onDelete(field.id)}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

export interface FieldDesignerProps {
    fields: DynamicField[];
    onChange: (fields: DynamicField[]) => void;
    onEdit: (field: DynamicField) => void;
    onAdd: () => void;
    isPlansAndCriteria?: boolean;
    isReadonly?: boolean;
}

export const FieldDesigner: React.FC<FieldDesignerProps> = ({ fields, onChange, onEdit, onAdd, isPlansAndCriteria, isReadonly }) => {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        if (isReadonly) return;
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = fields.findIndex((f) => f.id === active.id);
            const newIndex = fields.findIndex((f) => f.id === over.id);

            const newFields = arrayMove(fields, oldIndex, newIndex);

            // Update Order property
            const reorderedFields = newFields.map((f, index) => ({ ...f, order: index }));
            onChange(reorderedFields);
        }
    };

    const handleDelete = (id: string) => {
        if (!confirm('¿Seguro que deseas eliminar este campo?')) return;
        onChange(fields.filter(f => f.id !== id));
    };

    const handleEdit = (field: DynamicField) => {
        onEdit(field);
    };

    // Sort fields by order before rendering
    const sortedFields = [...fields].sort((a, b) => a.order - b.order);

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
        >
            <div className="flex flex-col w-full h-full space-y-5">
                <div className="flex items-center justify-between w-full">
                    {isReadonly ? (
                        <div className="bg-amber-50 border border-amber-200 flex gap-2 items-center px-4 py-2 rounded-lg">
                            <InformationCircleIcon className="w-6 h-6 text-amber-600 shrink-0" />
                            <p className="font-normal text-sm text-amber-700">
                                Los campos de las entidades de sistema son de solo lectura y no pueden modificarse.
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="bg-[#cffafe] border border-[#67e8f9] flex gap-2 items-center px-4 py-2 rounded-lg">
                                <InformationCircleIcon className="w-6 h-6 text-[#0e7490]" />
                                <p className="font-normal text-sm text-[#0e7490]">
                                    Arrastra las opciones para definir el orden en el formulario
                                </p>
                            </div>
                                <Button type="outline" size="sm" onClick={onAdd}>
                                {isPlansAndCriteria ? 'Nuevo Plan' : 'Nuevo Campo'}
                            </Button>
                        </>
                    )}
                </div>

                <div className="flex flex-col w-full border-t border-gray-200">
                    <SortableContext
                        items={sortedFields.map(f => f.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        {sortedFields.map((field) => (
                            <SortableItem
                                key={field.id}
                                id={field.id}
                                field={field}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                isReadonly={isReadonly}
                            />
                        ))}
                    </SortableContext>

                    {fields.length === 0 && (
                        <div className="text-center text-gray-400 py-12 border-b border-gray-200 bg-gray-50/30">
                            No hay campos definidos. Haz clic en "Nuevo" para comenzar.
                        </div>
                    )}
                </div>
            </div>
        </DndContext>
    );
};
